import { FC, ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Alert, Avatar, Button, ClickAwayListener, Fab, IconButton, Snackbar, Tooltip } from '@mui/material';
import { ArrowBackIos, ContentCopy, QuestionMark } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import copy from 'copy-to-clipboard';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector, useSaveSessionData, useSaveTimezone } from '../../lib';
import { CONTACT_EMAIL, YM_COUNTER } from '../../config';
import { configSelectors } from '../../store';
import { rootPaths } from '../../routes';

type LayoutProps = {
  title: ReactNode;
  actions?: ReactNode;
  className?: string;
};

export const Layout: FC<LayoutProps> = ({ children, title, actions, className }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isWix = useAppSelector(configSelectors.isWix);

  const [supportOpen, setSupportOpen] = useState(false);

  const user = useAppSelector((state) => state.app.wixUser);

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleUpgradeButtonClick = useCallback(() => {
    ym(YM_COUNTER, 'reachGoal', 'upgrade_button_click');
    Wix.Dashboard.openBillingPage();
  }, []);

  const handleTooltipClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleTooltipOpen = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const closeSupport = useCallback(() => {
    setSupportOpen(false);
  }, []);

  const openSupport = useCallback(() => {
    setSupportOpen(true);
  }, []);

  useSaveSessionData();
  useSaveTimezone();

  const showUpgradeButton = isWix && user?.plan === 'free' && user.embedScriptInjected;
  const showAvatar = isWix && user?.plan && user.plan !== 'free';
  const showBackButton = !rootPaths.includes(pathname);
  const shouldRenderSupportButton = isWix;

  return (
    <Wrapper>
      <Container className={className}>
        <Header>
          <HeaderSection>
            {showBackButton && (
              <BackButton
                onClick={() => {
                  navigate(-1);
                }}
                size="medium"
              >
                <ArrowBackIos fontSize="small" />
              </BackButton>
            )}

            <TitleSection>{title}</TitleSection>
          </HeaderSection>

          <ActionsSection>
            {showUpgradeButton && (
              <Button variant="contained" className="upgrade-button" onClick={handleUpgradeButtonClick}>
                Upgrade
              </Button>
            )}

            {actions}

            {showAvatar && (
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <div>
                  <Tooltip
                    title={
                      <TooltipContent>
                        <span>{t('user.currentPlan', { userPlan: user.plan?.toUpperCase() })}</span>

                        {user.plan !== 'premium' && (
                          <Button
                            size="small"
                            onClick={handleUpgradeButtonClick}
                            className="upgrade-button"
                            variant="contained"
                          >
                            {t('user.updatePlan', { userPlan: user.plan?.toUpperCase() })}
                          </Button>
                        )}
                      </TooltipContent>
                    }
                    onClose={handleTooltipClose}
                    arrow
                    open={tooltipOpen}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    classes={{
                      tooltip: 'user-plan-tooltip',
                      arrow: 'user-plan-tooltip-arrow',
                    }}
                  >
                    <UserPlan onClick={handleTooltipOpen}>{user.plan?.[0].toUpperCase()}</UserPlan>
                  </Tooltip>
                </div>
              </ClickAwayListener>
            )}
          </ActionsSection>
        </Header>

        {isWix && !user?.embedScriptInjected && (
          <FreePlanSiteAlert severity="warning">
            Probably, you have installed the application to free-plan site. This application works correctly only on
            premium-plan sites.
            <br />
            After upgrade your site to premium, please reinstall the application.
          </FreePlanSiteAlert>
        )}

        <ChilderWrapper>{children}</ChilderWrapper>

        {shouldRenderSupportButton && (
          <OpenSupportButton size="small" onClick={openSupport} color="secondary">
            <QuestionMark />
          </OpenSupportButton>
        )}

        <Snackbar
          open={supportOpen}
          onClose={closeSupport}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          ContentProps={{
            sx: {
              backgroundColor: '#fff',
            },
          }}
          sx={{ bottom: { xs: 72, sm: 72 } }}
          message={
            <SupportInfo>
              Do you want request a new feature?
              <br />
              Have questions or find a bug?
              <br />
              Contact us by email
              <EmailCopy
                onClick={() => {
                  copy(CONTACT_EMAIL);
                }}
              >
                <Email>{CONTACT_EMAIL}</Email>
                <IconButton size="small">
                  <ContentCopy fontSize="small" />
                </IconButton>
              </EmailCopy>
            </SupportInfo>
          }
        />
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  flex-grow: 1;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 48px;
  max-width: 1254px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const TitleSection = styled.div``;

const ActionsSection = styled.div`
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
  margin-left: 24px;

  .upgrade-button {
    background-color: #aa4dc8;

    + * {
      margin-left: 12px;
    }
  }
`;

const ChilderWrapper = styled.div`
  margin-top: 32px;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-end;
`;

const BackButton = styled(IconButton)`
  margin-right: 12px;
`;

const UserPlan = styled(Avatar)`
  margin-left: 12px;
  background-color: #aa4dc8;
  cursor: pointer;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .upgrade-button {
    margin-top: 4px;
    background-color: #aa4dc8;
  }
`;

const SupportInfo = styled.div`
  color: #000000de;
`;

const Email = styled.div`
  font-weight: 500;
  cursor: pointer;
  margin-right: 4px;
`;

const EmailCopy = styled.div`
  display: flex;
  align-items: center;
`;

const OpenSupportButton = styled(Fab)`
  position: fixed;
  bottom: 24px;
  right: 24px;
`;

const FreePlanSiteAlert = styled(Alert)`
  margin-top: 32px;
`;
